import { Component, Input, OnInit } from '@angular/core';
import { EventService, IDriverService, JobsService, TrucksService } from '../../../../services';
import { InputHelper } from '../../../../utils/input.helper';
import {
  JobScreenDriverDetailsDriver,
  JobScreenDriverDetailsTask,
  JobScreenDriverDetailsTruck,
} from '../../../../interfaces/screen-specific/job-screen';
import { Message } from '../../../../classes/Message';
import { MessageService } from 'primeng';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { ConfirmationService } from 'primeng/api';
import { JobScreenCommonService } from '../job-screen-common.service';

@Component({
  selector: 'job-screen-driver-details',
  templateUrl: './job-screen-driver-details.component.html',
  styleUrls: ['./job-screen-driver-details.component.scss', '../../common/job-screen-common.scss'],
})
export class JobScreenDriverDetailsComponent implements OnInit {
  @Input('jobId') jobId!: number;
  @Input('orderId') orderId!: number;
  @Input('driverDetailsTask') driverDetailsTask!: JobScreenDriverDetailsTask;
  @Input('organisationId') organisationId!: number;
  @Input('organisationTypeId') organisationTypeId!: number;

  isShowChangeDriverTruck: boolean;
  drivers: JobScreenDriverDetailsDriver[];
  selectedDriver: JobScreenDriverDetailsDriver;
  searchingDrivers: boolean;
  trucks: JobScreenDriverDetailsTruck[];
  selectedTruck: JobScreenDriverDetailsTruck;
  searchingTrucks: boolean;
  isBusyChangeDriverTruck: boolean;

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private eventService: EventService,
    public commonService: JobScreenCommonService,
    private driverService: IDriverService,
    private trucksService: TrucksService,
    private jobService: JobsService,
    public inputHelper: InputHelper,
  ) {}

  ngOnInit() {}

  showChangeDriverTruck() {
    if (this.driverDetailsTask.order.job.supplierId == null) {
      return;
    }

    this.isShowChangeDriverTruck = true;
    this.loadDrivers(this.driverDetailsTask.order.job.supplierId);
    this.loadTrucks(this.driverDetailsTask.order.job.supplierId);
  }

  loadDrivers(supplierId: number) {
    this.drivers = [];
    this.selectedDriver = null;
    this.searchingDrivers = true;

    this.driverService
      .getAll(supplierId)
      .then((result) => {
        this.searchingDrivers = false;

        let selectedDriver = null;
        let items = [];

        items.push({ label: 'Please Select', value: null });

        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.nickName, value: x });
          });

          if (this.driverDetailsTask.order.job.regionId != null) {
            items.forEach((x) => {
              if (
                x.value != null &&
                this.driverDetailsTask.driver != null &&
                x.value.id == this.driverDetailsTask.driver.id
              ) {
                selectedDriver = x.value;
              }
            });
          }
        }

        this.drivers = items;
        this.selectedDriver = selectedDriver;
      })
      .catch(() => {
        this.searchingDrivers = false;
        this.drivers = [];
        this.selectedDriver = null;
        this.messageService.add(
          Message.Error('An error occurred while loading Drivers, please try again.'),
        );
      });
  }

  loadTrucks(supplierId: number) {
    this.trucks = [];
    this.selectedTruck = null;
    this.searchingTrucks = true;

    this.trucksService
      .getAll(supplierId)
      .then((result) => {
        this.searchingTrucks = false;

        let selectedTruck = null;
        let items = [];

        items.push({ label: 'Please Select', value: null });

        if (result != null) {
          result.forEach((x: JobScreenDriverDetailsTruck) => {
            items.push({ label: x.model + '(' + x.registration + ')', value: x });
          });

          if (this.driverDetailsTask.order.job.regionId != null) {
            items.forEach((z) => {
              if (
                z.value != null &&
                this.driverDetailsTask.truck != null &&
                z.value.id == this.driverDetailsTask.truck.id
              ) {
                selectedTruck = z.value;
              }
            });
          }
        }

        this.trucks = items;
        this.selectedTruck = selectedTruck;
      })
      .catch(() => {
        this.searchingTrucks = false;
        this.trucks = [];
        this.selectedTruck = null;
        this.messageService.add(
          Message.Error('An error occurred while loading Trucks, please try again.', 'Load Trucks'),
        );
      });
  }

  public get driverName(): string {
    return this.driverNameTitle(false);
  }

  public get driverTitle(): string {
    return this.driverNameTitle(true);
  }

  public driverNameTitle(title: boolean) {
    let name: string = '';
    let driver = this.driverDetailsTask?.driver;
    if (driver != null) {
      let fullName = driver.firstName + ' ' + driver.surname;
      let nick =
        typeof driver.nickName != 'undefined' && driver.nickName ? driver.nickName : fullName;

      if (driver) {
        if (title) {
          name = this.organisationTypeId == OrganisationTypeEnum.Supplier ? fullName : nick;
        } else {
          name = this.organisationTypeId == OrganisationTypeEnum.Supplier ? nick : fullName;
        }
      }
    } else if (
      this.organisationTypeId == OrganisationTypeEnum.Customer ||
      this.organisationTypeId == OrganisationTypeEnum.Engineius
    ) {
      name =
        (this.driverDetailsTask?.order?.job?.supplier?.name || 'N/A') +
        (this.organisationTypeId == OrganisationTypeEnum.Engineius
          ? ' (Driver not yet assigned)'
          : '');
    }
    return name;
  }

  isDriverEqual(driver: JobScreenDriverDetailsDriver): boolean {
    if (this.driverDetailsTask == null) {
      return false;
    }

    if (driver == null && this.driverDetailsTask.driver == null) {
      return true;
    }

    if (
      (driver == null && this.driverDetailsTask.driver != null) ||
      (driver != null && this.driverDetailsTask.driver == null)
    ) {
      return false;
    }

    return driver.id == this.driverDetailsTask?.driver?.id;
  }

  isTruckEqual(truck: JobScreenDriverDetailsTruck): boolean {
    if (this.driverDetailsTask == null) {
      return false;
    }

    if (truck == null && this.driverDetailsTask.truck == null) {
      return true;
    }

    if (
      (truck == null && this.driverDetailsTask.truck != null) ||
      (truck != null && this.driverDetailsTask.truck == null)
    ) {
      return false;
    }

    return truck.id == this.driverDetailsTask?.truck?.id;
  }

  closeChangeDriverTruck() {
    this.isShowChangeDriverTruck = false;
  }

  private toDisplayWeightConfirmation(): boolean {
    return this.driverDetailsTask?.vehicle?.kerbWeight < 3500;
  }

  private async showConfirmationDialog(message: string, header: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        key: 'allocate-driver',
        message,
        header,
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  async SaveChangeDriverTruck() {
    this.isBusyChangeDriverTruck = true;

    if (this.toDisplayWeightConfirmation() && this.selectedDriver != null) {
      const confirmed = await this.showConfirmationDialog(
        `Are you sure ${this.selectedDriver?.firstName} ${this.selectedDriver?.surname} has the capability to carry out HGV movements?`,
        'Confirmation',
      );

      if (!confirmed) {
        this.isBusyChangeDriverTruck = false;
        return;
      }
    }

    this.jobService
      .changeDriverTruck({
        taskId: this.driverDetailsTask.id,
        driverId: this.selectedDriver != null ? this.selectedDriver.id : null,
        truckId: this.selectedTruck != null ? this.selectedTruck.id : null,
      })
      .then((x) => {
        this.isBusyChangeDriverTruck = false;
        this.driverDetailsTask.driver = this.selectedDriver;
        this.driverDetailsTask.truck = this.selectedTruck;

        /* DB 17/07/2024 -- Not displayed.. but we do need to update other components so emitting an update instead
        this.job.depot = this.selectedDepot;
        this.job.accountCodeList = this.selectedAccountCode;
         */

        this.eventService.JobScreenEvent.emit({
          event: 'job-details-changed',
          data: { jobId: this.jobId },
          sender: 'saveChangeTruckDriver',
        });
        this.eventService.JobScreenEvent.emit({
          event: 'refresh-notes',
          data: { jobId: this.jobId },
          sender: 'saveChangeTruckDriver',
        });

        this.messageService.add(Message.Success('Changes were successful.', 'Change Driver/Truck'));
        this.isShowChangeDriverTruck = false;
      })
      .catch((x) => {
        this.isBusyChangeDriverTruck = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Change Driver/Truck',
          ),
        );
      });
  }
}
